'use client'

import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { FlowButton } from '@/components/ui/flow-button'
import { RichText } from '@payloadcms/richtext-lexical/react'
import { motion } from 'framer-motion'

type Service = {
  id: string
  title: string
  content?: any
  image?: { url: string }
  alt?: string
  link?: string
  sections?: { icon?: { url: string }; title: string; description: string }[]
  ref_name?: string
}

type DynamicServicesSectionProps = {
  initialServices?: Service[]
  initialLocale?: 'en' | 'ar'
}

const DynamicServicesSection = ({
  initialServices = [],
  initialLocale,
}: DynamicServicesSectionProps) => {
  const { language } = useLanguage()
  const [services, setServices] = useState<Service[]>(initialServices)
  // Skip the client fetch on first mount if the server already gave us
  // data for the current language. Only refetch when the language changes.
  const hasHydratedLocale = useRef(initialLocale && initialLocale === language)

  useEffect(() => {
    if (hasHydratedLocale.current) {
      hasHydratedLocale.current = false
      return
    }

    let cancelled = false

    const fetchServices = async () => {
      try {
        const locale = language === 'ar' ? 'ar' : 'en'
        const res = await fetch(`/api/services?locale=${locale}&sort=createdAt&depth=2`)
        const json = await res.json()

        if (!cancelled && json?.docs?.length > 0) {
          const normalized = json.docs.map((doc: any) => ({
            id: doc.id,
            title: doc.title,
            ref_name: doc?.ref_name,
            content: doc.content,
            image: { url: doc.image?.thumbnailURL },
            alt: doc.image?.alt,
            link: doc.link || '/services',
            sections: doc.sections?.map((section: any) => ({
              title: section.title,
              description: section.description || '',
              icon: { url: section.icon?.thumbnailURL || '' },
            })),
          }))
          setServices(normalized)
        }
      } catch (err) {
        console.error('Error fetching services:', err)
      }
    }

    fetchServices()

    return () => {
      cancelled = true
    }
  }, [language])

  if (!services.length) return null

  return (
    <>
      {services.map((service, index) => {
        const isEven = index % 2 === 1
        const bgClass = isEven ? 'bg-black text-white' : 'bg-white text-[#414141]'
        const textColor = isEven ? 'text-white' : 'text-black'
        const descTextColor = isEven ? 'text-[#cbcbcb]' : 'text-[#666666]'

        return (
          <ServiceSection
            key={service.id}
            service={service}
            bgClass={bgClass}
            textColor={textColor}
            descTextColor={descTextColor}
            language={language}
          />
        )
      })}
    </>
  )
}

// Separate component for each service section to manage individual animations
const ServiceSection = ({
  service,
  bgClass,
  textColor,
  descTextColor,
  language,
}: {
  service: Service
  bgClass: string
  textColor: string
  descTextColor: string
  language: string
}) => {
  return (
    <section
      className={`relative px-4 sm:px-6 md:px-8 lg:px-12 xl:px-24 2xl:px-16 py-14 overflow-hidden md:pb-32 xl:pb-44 ${bgClass}`}
    >
      <div className="relative z-10 md:max-w-2xl lg:max-w-5xl xl:max-w-[1250px] 2xl:max-w-[1400px] mx-auto">
        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          viewport={{ once: true }}
          className={`text-[1.5rem] md:text-[1.8rem] lg:text-[1.75rem] xl:text-[2rem]  font-normal mb-4 xl:mb-6 mt-5 md:mt-10 lg:mt-12 ${textColor}`}
        >
          {service.title}
        </motion.h2>

        {/* RichText */}
        {service.content && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            viewport={{ once: true }}
            className={` text-[15px] xl:text-[15px] mb-8 lg:mb-16 xl:mb-20 !leading-[1.1rem] max-w-[480px] xl:max-w-[600px] ${bgClass.includes('bg-black') ? 'text-white' : 'text-[#444]'}`}
          >
            <RichText data={service.content} />
          </motion.div>
        )}

        {/* Mobile/Tablet Layout (Image First) */}
        <div className="lg:hidden">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            viewport={{ once: true }}
            className="relative w-full h-64 md:h-80 mb-8"
          >
            {service.image?.url && (
              <Image
                src={service.image.url}
                alt="Service Image"
                fill
                className="object-cover"
                sizes="100vw"
              />
            )}
          </motion.div>

          {/* Sections - One by one animation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {service.sections?.slice(0, 4).map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.3 + idx * 0.15,
                }}
                viewport={{ once: true }}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center gap-3">
                  {section.icon?.url && (
                    <div className="w-10 h-10 md:w-12 md:h-12">
                      <Image
                        src={section.icon.url}
                        alt={section.title}
                        width={48}
                        height={48}
                        className="object-contain"
                      />
                    </div>
                  )}
                  <h3 className={`text-[16px] md:text-[18px] font-normal ${textColor}`}>
                    {section.title}
                  </h3>
                </div>

                <p
                  className={`max-w-[450px] text-[14px] md:text-[13px] xl:text-[15px] !leading-[1.3rem] font-normal ${descTextColor}`}
                >
                  {section.description
                    .replace(/⦁|\u2022|\u25CF|\u25A0|\*/g, '')
                    .replace(/\n/g, ' ')}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          {service.id && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <Link href={`/services/${service.ref_name}`} className="self-start inline-block">
                <FlowButton text={language === 'ar' ? 'اعرف المزيد' : 'Learn more'} />
              </Link>
            </motion.div>
          )}
        </div>

        {/* Desktop Layout (lg and above - Original Layout) */}
        <div className="hidden lg:grid grid-cols-[60%_40%] gap-0 xl:gap-0 items-stretch">
          {/* Left: Sections */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            viewport={{ once: true }}
            className="flex flex-col justify-start gap-8 h-full"
          >
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 xl:gap-8">
              {service.sections?.slice(0, 4).map((section, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.3 + idx * 0.15,
                  }}
                  viewport={{ once: true }}
                  className="flex flex-col gap-3"
                >
                  {section.icon?.url && (
                    <div className="w-12 h-12 lg:w-14 lg:h-14 xl:w-20 xl:h-20 mb-0 xl:mb-2">
                      <Image
                        src={section.icon.url}
                        alt={section.title}
                        width={80}
                        height={80}
                        className="object-contain"
                      />
                    </div>
                  )}
                  <h3 className={`text-sm xl:text-xl font-normal xl:mb-1 ${textColor}`}>
                    {section.title}
                  </h3>
                  <p
                    className={`max-w-[200px] md:text-[11px] xl:text-[15px] !leading-[0.9rem] xl:!leading-[1.1rem] font-normal xl:mt-1 mb-5 lg:max-w-[250px] xl:max-w-[320px] ${descTextColor}`}
                  >
                    {section.description
                      .replace(/⦁|\u2022|\u25CF|\u25A0|\*/g, '')
                      .replace(/\n/g, ' ')}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            {service.id && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.8 }}
                viewport={{ once: true }}
              >
                <Link
                  href={`/services/${service.ref_name}`}
                  className="self-start inline-block"
                >
                  <FlowButton text={language === 'ar' ? 'اعرف المزيد' : 'Learn more'} />
                </Link>
              </motion.div>
            )}
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative w-full h-full min-h-[420px] lg:min-h-[480px] xl:min-h-[560px]"
          >
            {service.image?.url && (
              <Image
                src={service.image.url}
                alt={service.alt}
                fill
                className="object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            )}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default DynamicServicesSection
